import React from "react";

import {
  errorTextOf,
  normalizeExamV4Slice,
  type AllergyItem,
  type ExamV4Slice,
  type ProblemItem,
} from "./examModelV4";
import { CARD_NAMES } from "./examDispatchV4";
/* practice ของทีม: card อ่าน action จากไฟล์ sequence เท่านั้น (ห้าม import SDK) */
import {
  ACTIONS,
  SEQUENCE_NAME,
  type ExamRunParamsLoose,
  type ExamRunSequenceProp,
} from "../common/sequence/ExamV4";
import { AlertV4, ButtonV4, ModalV4 } from "../common/ui";

export type CardPatientFlagV4Props = {
  /** slice ของ ExamV4 (live mode เท่านั้น: mock mode ใช้ PatientPanelV4 ของเดิม) */
  exam?: Partial<ExamV4Slice> | null;
  /** สำรองกรณี slice ไม่มี patientId (MainHIS มี selectedEncounter.patient_id อยู่แล้ว) */
  patientId?: number;
  runSequence?: ExamRunSequenceProp;
  buttonLoadCheck?: Record<string, any>;
  errorMessage?: Record<string, any>;
  setProp?: (key: string, value: any) => void;
  onClose: () => void;
};

const statusText = (status: string): string => {
  switch (status) {
    case "NOTED":
      return "บันทึกแล้ว";
    case "PAUSED":
      return "ระงับ";
    case "CANCELED":
      return "ยกเลิก";
    case "LOCKED":
      return "ล็อก";
    case "NOT_KNOWN":
      return "ยังไม่ทราบ";
    default:
      return status;
  }
};

/**
 * ประวัติแพ้ (ADR) + โรคประจำตัว (problem list) ของ Patient Panel
 * V3 อยู่ที่ SubADRAndOther / CardAllergy (apps/HISV3/TPD/sequence/Allergy.ts)
 * contract ของ API lock ไว้ใน patient/examModelV4.ts (verify จาก staging API แล้ว)
 */
export const CardPatientFlagV4: React.FC<CardPatientFlagV4Props> = ({
  exam,
  patientId,
  runSequence,
  buttonLoadCheck,
  errorMessage,
  setProp,
  onClose,
}) => {
  const slice = exam === undefined || exam === null ? null : normalizeExamV4Slice(exam);
  const ids = slice ? { ...slice.ids, patientId: slice.ids.patientId ?? patientId } : { patientId };
  const cardName = CARD_NAMES.flags;

  const [adrText, setAdrText] = React.useState("");
  const [editingAdr, setEditingAdr] = React.useState<AllergyItem | null>(null);
  const [editAdrText, setEditAdrText] = React.useState("");
  const [problemText, setProblemText] = React.useState("");
  const [editingProblem, setEditingProblem] = React.useState<ProblemItem | null>(null);
  const [editProblemText, setEditProblemText] = React.useState("");

  const busy = (action: string): boolean =>
    buttonLoadCheck?.[`${cardName}_${action}`] === "LOADING";
  const centralError = errorTextOf(errorMessage?.[cardName]);

  const dispatch = (params: Record<string, unknown>): void => {
    runSequence?.(
      {
        ...params,
        sequence: SEQUENCE_NAME,
        card: cardName,
      } as unknown as ExamRunParamsLoose,
    );
  };

  const send = (params: Record<string, unknown>): void => {
    dispatch({
      ...ids,
      ...params,
      action: String(params.action ?? ""),
    });
  };

  const saveAdr = (): void => {
    const note = adrText.trim();
    if (note === "") return;
    send({ action: ACTIONS.SAVE_ADR, note });
    setAdrText("");
  };

  const saveEditedAdr = (): void => {
    if (editingAdr === null || editingAdr.id === null) return;
    const note = editAdrText.trim();
    if (note === "") return;
    send({ action: ACTIONS.UPDATE_ADR, adrId: editingAdr.id, note });
    setEditingAdr(null);
    setEditAdrText("");
  };

  const cancelAdr = (item: AllergyItem): void => {
    if (item.id === null) return;
    send({ action: ACTIONS.CANCEL_ADR, adrId: item.id, note: item.note });
  };

  const saveProblem = (): void => {
    const message = problemText.trim();
    if (message === "") return;
    send({ action: ACTIONS.SAVE_PROBLEM, message });
    setProblemText("");
  };

  const saveEditedProblem = (): void => {
    if (editingProblem === null || editingProblem.id === null) return;
    const message = editProblemText.trim();
    if (message === "") return;
    send({ action: ACTIONS.UPDATE_PROBLEM, problemId: editingProblem.id, message });
    setEditingProblem(null);
    setEditProblemText("");
  };

  const inactivateProblem = (item: ProblemItem): void => {
    if (item.id === null) return;
    send({ action: ACTIONS.INACTIVATE_PROBLEM, problemId: item.id });
  };

  const allergyItems = slice?.allergyItems ?? [];
  const problemItems = slice?.problemItems ?? [];

  return (
    <ModalV4 title="ประวัติแพ้ / โรคประจำตัว" width={760} onClose={onClose}>
      <section className="hv4-card">
        <h2 className="hv4-card-title">ประวัติแพ้ (Adverse Reaction)</h2>

        <div className="hv4-flag-list">
          {allergyItems.map((item, index) => (
            <div key={`${item.id ?? index}`} className="hv4-flag-list-item">
              <div>
                <b>{item.name === "" ? "ประวัติแพ้ (ไม่มีชื่อยา)" : item.name}</b>
                {item.name === "" ? "" : ` · ${item.note}`}
                {item.name === "" ? "" : ""}
                <span className="hv4-flag-list-status"> ({statusText(item.status)})</span>
              </div>
              {editingAdr !== null && editingAdr.id === item.id ? (
                <div>
                  <textarea
                    className="hv4-input hv4-textarea"
                    rows={2}
                    aria-label="แก้ประวัติแพ้"
                    value={editAdrText}
                    onChange={(event) => setEditAdrText(event.target.value)}
                  />
                  <ButtonV4 variant="ok" size="sm" disabled={busy(ACTIONS.UPDATE_ADR)} onClick={saveEditedAdr}>
                    บันทึกการแก้ไข
                  </ButtonV4>
                  <ButtonV4
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditingAdr(null);
                      setEditAdrText("");
                    }}
                  >
                    ยกเลิกการแก้ไข
                  </ButtonV4>
                </div>
              ) : (
                <div>
                  {item.note !== "" && <div className="hv4-flag-list-note">{item.note}</div>}
                  <ButtonV4
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditingAdr(item);
                      setEditAdrText(item.note === "" ? item.name : item.note);
                    }}
                  >
                    แก้ไข
                  </ButtonV4>
                  <ButtonV4 variant="danger-ghost" size="sm" onClick={() => cancelAdr(item)}>
                    ปิดใช้งาน
                  </ButtonV4>
                </div>
              )}
            </div>
          ))}
          {allergyItems.length === 0 && (
            <div className="hv4-empty">ยังไม่มีประวัติแพ้ของผู้ป่วยรายนี้</div>
          )}
        </div>

        <div className="hv4-flag-add">
          <input
            className="hv4-input"
            aria-label="เพิ่มประวัติแพ้"
            placeholder="เช่น Penicillin (ผื่น, ลมพิษ)"
            value={adrText}
            onChange={(event) => setAdrText(event.target.value)}
          />
          <ButtonV4
            variant="primary"
            disabled={adrText.trim() === "" || busy(ACTIONS.SAVE_ADR)}
            onClick={saveAdr}
          >
            + เพิ่มประวัติแพ้
          </ButtonV4>
        </div>

        {busy(ACTIONS.SAVE_ADR) && (
          <div role="status" className="hv4-note hv4-mt-8">
            กำลังบันทึกประวัติแพ้…
          </div>
        )}
        {centralError !== null && (
          <AlertV4 tone="danger" className="hv4-mt-8" onClose={() => setProp?.(`errorMessage.${cardName}`, null)}>
            {centralError}
          </AlertV4>
        )}
      </section>

      <section className="hv4-card hv4-mt-12">
        <h2 className="hv4-card-title">โรคประจำตัว (Problem List)</h2>

        <div className="hv4-flag-list">
          {problemItems.map((item, index) => (
            <div key={`${item.id ?? index}`} className="hv4-flag-list-item">
              <div>
                <b>{item.message}</b>
                {item.edited !== "" && (
                  <span className="hv4-flag-list-status"> · แก้ไขเมื่อ {item.edited}</span>
                )}
              </div>
              {editingProblem !== null && editingProblem.id === item.id ? (
                <div>
                  <input
                    className="hv4-input"
                    aria-label="แก้โรคประจำตัว"
                    value={editProblemText}
                    onChange={(event) => setEditProblemText(event.target.value)}
                  />
                  <ButtonV4
                    variant="ok"
                    size="sm"
                    disabled={busy(ACTIONS.UPDATE_PROBLEM)}
                    onClick={saveEditedProblem}
                  >
                    บันทึกการแก้ไข
                  </ButtonV4>
                  <ButtonV4
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditingProblem(null);
                      setEditProblemText("");
                    }}
                  >
                    ยกเลิกการแก้ไข
                  </ButtonV4>
                </div>
              ) : (
                <div>
                  <ButtonV4
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditingProblem(item);
                      setEditProblemText(item.message);
                    }}
                  >
                    แก้ไข
                  </ButtonV4>
                  <ButtonV4 variant="danger-ghost" size="sm" onClick={() => inactivateProblem(item)}>
                    ปิดใช้งาน
                  </ButtonV4>
                </div>
              )}
            </div>
          ))}
          {problemItems.length === 0 && <div className="hv4-empty">ยังไม่มีโรคประจำตัวของผู้ป่วยรายนี้</div>}
        </div>

        <div className="hv4-flag-add">
          <input
            className="hv4-input"
            aria-label="เพิ่มโรคประจำตัว"
            placeholder="เช่น ความดันโลหิตสูง"
            value={problemText}
            onChange={(event) => setProblemText(event.target.value)}
          />
          <ButtonV4
            variant="primary"
            disabled={problemText.trim() === "" || busy(ACTIONS.SAVE_PROBLEM)}
            onClick={saveProblem}
          >
            + เพิ่มโรคประจำตัว
          </ButtonV4>
        </div>

        {(busy(ACTIONS.SAVE_PROBLEM) || busy(ACTIONS.UPDATE_PROBLEM)) && (
          <div role="status" className="hv4-note hv4-mt-8">
            กำลังบันทึกลำดับโรค…
          </div>
        )}
        {(busy(ACTIONS.INACTIVATE_PROBLEM) || busy(ACTIONS.CANCEL_ADR)) && (
          <div role="status" className="hv4-note hv4-mt-8">
            กำลังปิดใช้งานรายการ…
          </div>
        )}
      </section>

      <div className="hv4-mt-12">
        <ButtonV4 variant="ghost" onClick={onClose}>
          ปิด
        </ButtonV4>
      </div>
    </ModalV4>
  );
};

export default CardPatientFlagV4;
